import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah, tanggalID, todayISO } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Factory, FileText, FileSpreadsheet } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";
import { API_BASE } from "@/lib/api";

const empty = { tanggal: todayISO(), biaya_bahan_baku: 0, biaya_operasional: 0, output_kg: 0, keterangan: "" };

export default function Produksi() {
  const [items, setItems] = useState([]);
  const [sisaStok, setSisaStok] = useState(0);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try { const r = await api.get("/produksi"); setItems(r.data); }
    catch(e){ toast.error(formatApiError(e)); }
    try {
      const d = await api.get("/dashboard/arang");
      setSisaStok(d.data.sisa_stok_kg);
    } catch (_e) { /* dashboard-arang may 403 on non-arang; safe to ignore */ }
  };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e && e.target ? e.target.value : e });

  const openNew = () => { setEditId(null); setForm({ ...empty, tanggal: todayISO() }); setOpen(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({ ...empty, ...it }); setOpen(true); };

  const hpp = (() => {
    const kg = Number(form.output_kg || 0);
    const total = Number(form.biaya_bahan_baku || 0) + Number(form.biaya_operasional || 0);
    return kg > 0 ? Math.round(total / kg) : 0;
  })();
  const totalModal = Number(form.biaya_bahan_baku || 0) + Number(form.biaya_operasional || 0);

  const save = async () => {
    if (!form.tanggal) { toast.error("Tanggal wajib diisi"); return; }
    if (!(Number(form.output_kg) > 0)) { toast.error("Hasil Arang (Kg) wajib diisi (lebih dari 0)"); return; }
    const payload = {
      tanggal: form.tanggal,
      biaya_bahan_baku: Number(form.biaya_bahan_baku || 0),
      biaya_operasional: Number(form.biaya_operasional || 0),
      output_kg: Number(form.output_kg || 0),
      keterangan: form.keterangan || "",
    };
    try {
      if (editId) await api.put(`/produksi/${editId}`, payload);
      else await api.post("/produksi", payload);
      toast.success(editId ? "Produksi diperbarui" : "Produksi tersimpan (biaya otomatis masuk ke Pengeluaran)");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus data produksi? Pengeluaran yang terkait juga akan dipindah ke Sampah.")) return;
    try { await api.delete(`/produksi/${id}`); toast.success("Terhapus"); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  const totalKg = items.reduce((a, x) => a + Number(x.output_kg || 0), 0);
  const totalBiaya = items.reduce((a, x) => a + Number(x.total_biaya || 0), 0);
  const hppAvg = totalKg > 0 ? Math.round(totalBiaya / totalKg) : 0;
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(items, 10);

  const exportFile = (kind, fmt) => {
    const token = localStorage.getItem("khn_token");
    const url = `${API_BASE}/export/${kind}?fmt=${fmt}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `${kind}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
        a.click();
      });
  };

  return (<>
    <PageHeader title="Produksi Arang" subtitle="Catat produksi & HPP per Kg. Biaya otomatis tersinkron ke Pengeluaran."
      actions={<>
        <Button variant="outline" onClick={()=>exportFile("produksi-bulanan","pdf")} className="rounded-sm" data-testid="export-prod-pdf"><FileText size={14} className="mr-1"/>PDF</Button>
        <Button variant="outline" onClick={()=>exportFile("produksi-bulanan","xlsx")} className="rounded-sm" data-testid="export-prod-xlsx"><FileSpreadsheet size={14} className="mr-1"/>Excel</Button>
        <Button variant="outline" onClick={()=>exportFile("penjualan-per-pembeli","pdf")} className="rounded-sm" data-testid="export-penj-pdf"><FileText size={14} className="mr-1"/>Rekap Penjualan</Button>
        <Button onClick={openNew} className="brand-btn rounded-sm" data-testid="add-produksi-btn"><Plus size={14} className="mr-1"/>Tambah Produksi</Button>
      </>}/>
    <div className="p-4 md:p-8 space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
        <div className="card-flat p-4" data-testid="stat-total-produksi">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Total Produksi</div>
          <div className="mt-3 font-mono text-lg md:text-2xl text-slate-900">{totalKg.toLocaleString("id-ID")} Kg</div>
        </div>
        <div className="card-flat p-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Total Biaya</div>
          <div className="mt-3 font-mono text-lg md:text-2xl text-slate-900">{rupiah(totalBiaya)}</div>
        </div>
        <div className="card-flat p-4" data-testid="stat-hpp-avg">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">HPP Rata-rata</div>
          <div className="mt-3 font-mono text-lg md:text-2xl brand-text">{rupiah(hppAvg)}/Kg</div>
        </div>
        <div className="card-flat p-4" data-testid="stat-sisa-stok">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Sisa Stok (Kg)</div>
          <div className="mt-3 font-mono text-lg md:text-2xl brand-text font-semibold">{Number(sisaStok||0).toLocaleString("id-ID")} Kg</div>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-xs md:text-sm" data-testid="produksi-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="p-3">Tanggal</th><th className="p-3 text-right">Biaya Bahan</th>
              <th className="p-3 text-right">Biaya Operasional</th><th className="p-3 text-right">Hasil Arang (Kg)</th>
              <th className="p-3 text-right">HPP/Kg</th><th className="p-3 text-right">Total Modal</th><th className="p-3 hide-mobile">Keterangan</th>
              <th className="p-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-slate-400">Belum ada data produksi. Klik Tambah Produksi.</td></tr>}
            {paged.map(it => (
              <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`produksi-row-${it.id}`}>
                <td className="p-3 whitespace-nowrap">{tanggalID(it.tanggal)}</td>
                <td className="p-3 text-right font-mono">{rupiah(it.biaya_bahan_baku)}</td>
                <td className="p-3 text-right font-mono">{rupiah(it.biaya_operasional)}</td>
                <td className="p-3 text-right font-mono font-semibold">{Number(it.output_kg||0).toLocaleString("id-ID")} Kg</td>
                <td className="p-3 text-right font-mono brand-text font-semibold">{rupiah(it.hpp_per_kg)}</td>
                <td className="p-3 text-right font-mono font-semibold">{rupiah(it.total_biaya)}</td>
                <td className="p-3 text-slate-500 hide-mobile">{it.keterangan || "-"}</td>
                <td className="p-3">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={()=>openEdit(it)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-produksi-${it.id}`}><Pencil size={14}/></button>
                    <button onClick={()=>del(it.id)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-produksi-${it.id}`}><Trash2 size={14} className="text-rose-600"/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="produksi-pagination"/>
      </div>
    </div>

    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="produksi-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Factory size={18} className="brand-text"/>{editId?"Ubah":"Tambah"} Produksi</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Tanggal <span className="text-rose-500">*</span></Label><Input type="date" value={form.tanggal} onChange={upd("tanggal")} data-testid="prod-tanggal"/></div>
          <div className="space-y-1.5"><Label>Hasil Arang (Kg) <span className="text-rose-500">*</span></Label><Input type="number" min="0" step="0.01" value={form.output_kg} onChange={upd("output_kg")} data-testid="prod-output-kg"/></div>
          <div className="space-y-1.5"><Label>Biaya Bahan Baku (Rp)</Label><CurrencyInput value={form.biaya_bahan_baku} onValueChange={upd("biaya_bahan_baku")} data-testid="prod-biaya-bahan"/></div>
          <div className="space-y-1.5"><Label>Biaya Operasional (Rp)</Label><CurrencyInput value={form.biaya_operasional} onValueChange={upd("biaya_operasional")} data-testid="prod-biaya-op"/></div>
          <div className="md:col-span-2 grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>HPP / Kg (otomatis)</Label>
              <div className="h-9 px-3 flex items-center border border-slate-200 bg-blue-50 font-mono font-semibold brand-text" data-testid="prod-hpp">
                {rupiah(hpp)} / Kg
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Total Modal (otomatis)</Label>
              <div className="h-9 px-3 flex items-center border border-slate-200 bg-slate-50 font-mono font-semibold text-slate-900" data-testid="prod-total-modal">
                {rupiah(totalModal)}
              </div>
            </div>
          </div>
          <div className="md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")}/></div>
        </div>
        <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded-sm p-2">
          Biaya bahan baku & operasional otomatis dibuatkan entri di menu Pengeluaran (auto-sync).
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Batal</Button>
          <Button onClick={save} className="brand-btn rounded-sm" data-testid="produksi-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>);
}
